import * as React from "react"
import { cva, type VariantProps } from "class-variance-authority"
import { Slot } from "radix-ui"

import { cn } from "@/lib/utils"

const buttonVariants = cva(
  "inline-flex shrink-0 items-center justify-center gap-1.5 rounded-full font-sans text-[16px] font-medium whitespace-nowrap transition-[background-color,color,box-shadow] outline-none select-none focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-black disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-4",
  {
    variants: {
      variant: {
        default: "bg-black px-3 py-2 text-white hover:bg-[#1a1a1a]",
        outline:
          "border border-black/10 bg-[#efefef] px-4 py-2 text-black hover:bg-[#e2e2e2]",
        secondary:
          "border border-black/10 bg-white px-3 py-2 text-black shadow-[rgba(0,0,0,0.12)_0px_4px_16px_0px] hover:bg-[#e2e2e2]",
        ghost:
          "bg-transparent text-black hover:bg-[#f3f3f3]",
        destructive:
          "bg-black px-3 py-2 text-white hover:bg-[#1a1a1a]",
        link: "rounded-none px-0 py-0 text-[14px] text-black underline underline-offset-2 hover:text-black",
        pill: "border border-black/10 bg-[#efefef] px-4 py-2 text-[14px] text-black hover:bg-[#e2e2e2]",
        pillDark: "bg-black px-4 py-2 text-[14px] text-white hover:bg-[#1a1a1a]",
      },
      size: {
        default: "h-auto",
        xs: "px-3 py-1.5 text-[12px]",
        sm: "px-3 py-2 text-[14px]",
        lg: "px-6 py-3 text-[18px]",
        icon: "size-10",
        "icon-xs": "size-6 text-[12px]",
        "icon-sm": "size-9 text-[14px]",
        "icon-lg": "size-12",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  }
)

function Button({
  className,
  variant = "default",
  size = "default",
  asChild = false,
  ...props
}: React.ComponentProps<"button"> &
  VariantProps<typeof buttonVariants> & {
    asChild?: boolean
  }) {
  const Comp = asChild ? Slot.Root : "button"

  return (
    <Comp
      data-slot="button"
      data-variant={variant}
      data-size={size}
      className={cn(buttonVariants({ variant, size, className }))}
      {...props}
    />
  )
}

export { Button, buttonVariants }
